/**
 * RunDetail — feuille de temps (heures par employé), calcul, approbation, paiement + exports. Batch #14.250.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Calculator, CheckCircle2, RotateCcw, Banknote, Trash2, Plus, ChevronDown, ChevronRight, Save, UserPlus, AlertTriangle, Lock, Pencil, RefreshCw } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, fmtCAD, FREQ_LABELS, StatusBadge, Btn, inputCls, num, err, thCls, tdCls } from "./_shared";
import StubBreakdown from "./StubBreakdown";
import RunExports from "./RunExports";
import RunEditForm from "./RunEditForm";
import CcqEntryExtra from "./CcqEntryExtra";
import RateHoursBlock, { rateHoursBody } from "./RateHoursBlock";
import FixRuleLink from "./ccq/FixRuleLink";
import { recalculateOpenRuns } from "./ccq/CcqStaleRunsBanner";

const HOUR_COLS = [["regular_hours", "Rég."], ["ot15_hours", "× 1,5"], ["ot2_hours", "× 2"], ["holiday_hours", "Férié"], ["sick_hours", "Maladie"], ["vacation_hours", "Vac. (h)"]];
const AMT_COLS = [["bonus", "Boni"], ["commission", "Commission"], ["vacation_payout", "Vac. payées ($)"], ["taxable_benefits", "Av. imposable"]];

function entryForm(en) {
  const f = { ...en };
  [...HOUR_COLS, ...AMT_COLS].forEach(([k]) => { f[k] = en[k] ?? 0; });
  f.other_earnings = en.other_earnings || []; f.one_time_deductions = en.one_time_deductions || [];
  f.ccq_assignment = en.ccq_assignment || {}; f.ccq_premiums = en.ccq_premiums || []; f.ccq_travel = en.ccq_travel || []; f.ccq_sites = en.ccq_sites || [];
  f.rate_hours = en.rate_hours || [];
  return f;
}

function ExtraRow({ f, set, dedTypes, locked, eid, ccq, employee, asOf }) {
  const setList = (key, i, k, v) => set(key, f[key].map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-3">
    <RateHoursBlock rates={employee?.extra_rates} rows={f.rate_hours} onChange={(v) => set("rate_hours", v)} locked={locked || f.excluded} eid={eid} />
    {ccq && employee?.ccq?.active && <CcqEntryExtra f={f} set={set} locked={locked} cfg={ccq.cfg} meta={ccq.meta} eid={eid} employee={employee} asOf={asOf} sites={ccq.sites} />}
    <div className="grid lg:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`paie-ts-extra-${eid}`}>
      <div className="space-y-1.5">
        <div className="text-[10px] font-bold uppercase text-slate-500">Autres gains</div>
        {f.other_earnings.map((o, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input className={inputCls} placeholder="Libellé (ex. prime de quart)" value={o.label || ""} disabled={locked} onChange={(e) => setList("other_earnings", i, "label", e.target.value)} />
            <input type="number" step="0.01" className={`${inputCls} w-28`} placeholder="$" value={o.amount ?? ""} disabled={locked} onChange={(e) => setList("other_earnings", i, "amount", e.target.value)} />
            <label className="text-[10px] text-slate-500 whitespace-nowrap inline-flex items-center gap-1"><input type="checkbox" checked={o.vacationable ?? false} disabled={locked} onChange={(e) => setList("other_earnings", i, "vacationable", e.target.checked)} /> vac.</label>
            {!locked && <button onClick={() => set("other_earnings", f.other_earnings.filter((_, j) => j !== i))} className="text-rose-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
        {!locked && <Btn tone="ghost" icon={Plus} onClick={() => set("other_earnings", [...f.other_earnings, { label: "", amount: "", taxable: true, pensionable: true, insurable: true, vacationable: false }])} testId={`paie-ts-add-earning-${eid}`}>Ajouter un gain</Btn>}
      </div>
      <div className="space-y-1.5">
        <div className="text-[10px] font-bold uppercase text-slate-500">Retenues ponctuelles (cette paie seulement)</div>
        {f.one_time_deductions.map((o, i) => (
          <div key={i} className="flex gap-2 items-center">
            <select className={inputCls} value={o.code || ""} disabled={locked} onChange={(e) => setList("one_time_deductions", i, "code", e.target.value)} data-testid={`paie-ts-ded-code-${eid}-${i}`}>
              {dedTypes.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
            <input type="number" step="0.01" className={`${inputCls} w-28`} placeholder="$" value={o.amount ?? ""} disabled={locked} onChange={(e) => setList("one_time_deductions", i, "amount", e.target.value)} data-testid={`paie-ts-ded-amount-${eid}-${i}`} />
            {o.label && <span className="text-[10px] text-slate-500 whitespace-nowrap max-w-[140px] truncate" title={o.label}>« {o.label} »</span>}
            {!locked && <button onClick={() => set("one_time_deductions", f.one_time_deductions.filter((_, j) => j !== i))} className="text-rose-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
        {!locked && <Btn tone="ghost" icon={Plus} onClick={() => set("one_time_deductions", [...f.one_time_deductions, { code: "advance", amount: "" }])} testId={`paie-ts-add-ded-${eid}`}>Ajouter une retenue</Btn>}
        <input className={inputCls} placeholder="Note pour cet employé" value={f.note || ""} disabled={locked} onChange={(e) => set("note", e.target.value)} />
      </div>
    </div>
    </div>
  );
}

export default function RunDetail({ masterId, runId, settings, onChanged }) {
  const [data, setData] = useState(null);
  const [forms, setForms] = useState({});
  const [dirty, setDirty] = useState({});
  const [open, setOpen] = useState({});
  const scrollRef = useRef(null);
  const [visibleW, setVisibleW] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setVisibleW(el.clientWidth));
    ro.observe(el);
    setVisibleW(el.clientWidth);
    return () => ro.disconnect();
  }, [data]);
  const [busy, setBusy] = useState("");
  const [dedTypes, setDedTypes] = useState([]);
  const [allEmps, setAllEmps] = useState([]);
  const [addId, setAddId] = useState("");
  const [editDates, setEditDates] = useState(false);
  const [ccq, setCcq] = useState(null);
  const base = `/admin/payroll/${masterId}/runs/${runId}`;

  const load = useCallback(async () => {
    try {
      const [r, dt, em] = await Promise.all([
        axios.get(`${API}${base}`, { headers: authH() }),
        axios.get(`${API}/admin/payroll/${masterId}/deduction-types`, { headers: authH() }),
        axios.get(`${API}/admin/payroll/${masterId}/employees`, { headers: authH() }),
      ]);
      setData(r.data);
      setForms(Object.fromEntries(r.data.rows.map((x) => [x.employee.id, entryForm(x.entry)])));
      setDirty({});
      setDedTypes(dt.data.items || []);
      setAllEmps(em.data.items || []);
      if (settings.ccq_enabled && r.data.rows.some((x) => x.employee.ccq?.active)) {
        const [cc, cm, cs] = await Promise.all([axios.get(`${API}/admin/payroll/${masterId}/ccq/config`, { headers: authH() }), axios.get(`${API}/admin/payroll/ccq/meta`, { headers: authH() }),
          axios.get(`${API}/admin/payroll/${masterId}/ccq/sites`, { headers: authH() })]);
        setCcq({ cfg: cc.data, meta: cm.data, sites: cs.data.items || [] });
      }
    } catch (e) { err(e, "Période introuvable"); }
  }, [base, masterId, settings.ccq_enabled]);
  useEffect(() => { load(); }, [load]);

  const run = data?.run;
  const locked = !run || !["draft", "calculated"].includes(run.status);
  const setField = (eid, k, v) => {
    setForms((f) => {
      const next = { ...f[eid], [k]: v };
      if (k === "ccq_sites" && (v || []).length) {  // les totaux d'heures suivent la répartition par chantier
        ["regular_hours", "ot15_hours", "ot2_hours"].forEach((h) => { next[h] = v.reduce((a, r) => a + (num(r[h]) || 0), 0); });
      }
      return { ...f, [eid]: next };
    });
    setDirty((d) => ({ ...d, [eid]: true }));
  };

  const saveDirty = async () => {
    const ids = Object.keys(dirty).filter((k) => dirty[k]);
    for (const eid of ids) {
      const f = forms[eid];
      const body = { ...f };
      [...HOUR_COLS, ...AMT_COLS].forEach(([k]) => { body[k] = num(f[k]); });
      body.other_earnings = f.other_earnings.filter((o) => num(o.amount) > 0).map((o) => ({ ...o, amount: num(o.amount) }));
      body.one_time_deductions = f.one_time_deductions.filter((o) => o.code && num(o.amount) > 0).map((o) => ({ code: o.code, amount: num(o.amount), ...(o.label ? { label: o.label } : {}) }));
      body.ccq_premiums = (f.ccq_premiums || []).filter((p) => p.code && p.qty !== "").map((p) => ({ code: p.code, qty: num(p.qty) }));
      body.ccq_travel = (f.ccq_travel || []).filter((t) => t.rule_id && num(t.qty) > 0).map((t) => ({ rule_id: t.rule_id, qty: num(t.qty) }));
      body.ccq_assignment = { ...(f.ccq_assignment || {}), period: num(f.ccq_assignment?.period) || null };
      body.ccq_sites = (f.ccq_sites || []).filter((s) => s.site_id).map((s) => ({ site_id: s.site_id, regular_hours: num(s.regular_hours), ot15_hours: num(s.ot15_hours), ot2_hours: num(s.ot2_hours) }));
      body.rate_hours = rateHoursBody(f.rate_hours);
      ["id", "master_id", "run_id", "employee_id", "created_at", "updated_at"].forEach((k) => delete body[k]);
      await axios.put(`${API}${base}/entries/${eid}`, body, { headers: authH() });
    }
    return ids.length;
  };

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); onChanged?.(); } catch (e) { err(e); } finally { setBusy(""); }
  };
  const saveHours = () => act("save", async () => { const n = await saveDirty(); toast.success(`${n} feuille(s) de temps enregistrée(s)`); });
  const calculate = () => act("calc", async () => {
    await saveDirty();
    const r = await axios.post(`${API}${base}/calculate`, {}, { headers: authH() });
    (r.data.warnings || []).forEach((w) => toast.message(w));
    toast.success(`Calcul terminé — ${r.data.nb_stubs} bulletin(s) · net ${fmtCAD(r.data.totals.net)}`);
  });
  const approve = () => window.confirm("Approuver la période ? Les heures seront verrouillées (réouverture possible tant qu'aucune période postérieure n'est approuvée).")
    && act("approve", async () => { const r = await axios.post(`${API}${base}/approve`, {}, { headers: authH() }); if (r.data.warning) toast.message(r.data.warning); }, "Période approuvée");
  const reopen = () => window.confirm("Rouvrir la période ? Les bulletins repassent en « calculée ».") && act("reopen", () => axios.post(`${API}${base}/reopen`, {}, { headers: authH() }), "Période rouverte");
  const markPaid = () => window.confirm(`Marquer la période comme payée (paie du ${run.pay_date}) ?${settings.paystub_delivery !== "manual" ? "\n\nLes bulletins seront envoyés automatiquement selon le mode configuré." : ""}`)
    && act("paid", () => axios.post(`${API}${base}/mark-paid`, {}, { headers: authH() }), "Période marquée payée");
  const removeEmp = (eid) => window.confirm("Retirer cet employé de la période ?") && act(`rm-${eid}`, () => axios.delete(`${API}${base}/employees/${eid}`, { headers: authH() }));
  const addEmp = () => addId && act("add", () => axios.post(`${API}${base}/employees/${addId}`, {}, { headers: authH() }).then(() => setAddId("")), "Employé ajouté à la période");
  const recalcAll = () => act("recalc-all", () => recalculateOpenRuns(masterId));

  const inRun = useMemo(() => new Set((data?.rows || []).map((x) => x.employee.id)), [data]);
  const addable = allEmps.filter((e) => !inRun.has(e.id));
  const totals = run?.totals;
  const hasDirty = Object.values(dirty).some(Boolean);

  if (!data) return <p className="text-sm text-slate-500 p-6">Chargement…</p>;
  return (
    <div className="space-y-4" data-testid="paie-run-detail">
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-bold text-slate-900">Période {run.period_start} → {run.period_end}</h2>
            <StatusBadge status={run.status} testId="paie-run-detail-status" />
            {locked && <Lock className="w-3.5 h-3.5 text-slate-400" />}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">Paie le <b>{run.pay_date}</b> · {FREQ_LABELS[run.frequency]} · #{run.year}-{String(run.sequence).padStart(3, "0")} · {run.nb_employees} employé(s){run.note ? ` · ${run.note}` : ""}</p>
          {(run.source === "portal" || run.client_submitted_at) && (
            <p className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-2 py-1 mt-1 inline-block" data-testid="paie-run-client-info">
              {run.source === "portal" ? "Créée par le client dans son portail" : "Créée côté gestion"}{run.client_submitted_at ? ` · heures soumises par le client le ${String(run.client_submitted_at).slice(0, 16).replace("T", " ")}` : ""}
            </p>
          )}
          {totals && <p className="text-xs text-slate-600 mt-1 font-mono" data-testid="paie-run-totals">Brut {fmtCAD(totals.gross)} · Retenues {fmtCAD(totals.gross - totals.net)} · <b>Net {fmtCAD(totals.net)}</b> · Employeur {fmtCAD(totals.employer)} · Coût total {fmtCAD(totals.total_cost)} · {totals.hours} h</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {!locked && <Btn tone="slate" icon={Pencil} onClick={() => setEditDates((v) => !v)} testId="paie-run-edit-dates">Modifier les dates</Btn>}
          {!locked && <Btn tone="slate" icon={Save} onClick={saveHours} busy={busy === "save"} disabled={!hasDirty} testId="paie-run-save-hours">Enregistrer les heures</Btn>}
          {!locked && <Btn tone="emerald" icon={Calculator} onClick={calculate} busy={busy === "calc"} testId="paie-run-calculate">{run.status === "calculated" ? "Recalculer" : "Calculer la paie"}</Btn>}
          {run.status === "calculated" && <Btn tone="orange" icon={CheckCircle2} onClick={approve} busy={busy === "approve"} disabled={hasDirty} testId="paie-run-approve">Approuver</Btn>}
          {run.status === "approved" && <Btn tone="orange" icon={Banknote} onClick={markPaid} busy={busy === "paid"} testId="paie-run-mark-paid">Marquer payée</Btn>}
          {["approved", "paid"].includes(run.status) && <Btn tone="slate" icon={RotateCcw} onClick={reopen} busy={busy === "reopen"} testId="paie-run-reopen">Rouvrir</Btn>}
        </div>
      </div>

      {editDates && !locked && <RunEditForm masterId={masterId} run={run} onDone={() => { setEditDates(false); load(); onChanged?.(); }} onCancel={() => setEditDates(false)} />}

      {(run.holidays || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="paie-run-holidays">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <b>Jour(s) férié(s) dans cette période :</b> {run.holidays.map((h) => `${h.label} (${h.date})`).join(" · ")}.
            <span className="block text-amber-800/80">Le férié est payé : saisissez les heures dans la colonne « Férié » (indemnité : 1/20 du salaire gagné dans les 4 semaines précédentes, ou la journée normale).</span>
          </div>
        </div>
      )}

      {data.ccq_config_changed_since_calc && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-900 flex flex-col lg:flex-row lg:items-center gap-2" data-testid="paie-run-ccq-stale">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 hidden lg:block" />
          <div className="flex-1">
            <b>Configuration CCQ modifiée le {String(data.ccq_config_updated_at).slice(0, 16).replace("T", " ")}, après le calcul de cette période ({String(run.calculated_at).slice(0, 16).replace("T", " ")}).</b>
            <span className="block text-rose-800/80">Les bulletins reflètent les anciennes règles : {locked ? "rouvrez la période puis " : ""}cliquez « Recalculer » pour appliquer les cotisations à jour (avantages sociaux, taxe, fonds…).</span>
          </div>
          <Btn tone="rose" icon={RefreshCw} onClick={recalcAll} busy={busy === "recalc-all"} testId="paie-run-recalc-all">Recalculer toutes les périodes ouvertes</Btn>
        </div>
      )}

      {(run.ccq_issues || []).length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-900 space-y-1" data-testid="paie-run-ccq-issues">
          <div className="font-bold flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Cotisations CCQ configurées mais non appliquées dans cette période ({run.ccq_issues.length})</div>
          {run.ccq_issues.map((it, i) => (
            <div key={`${it.employee_id}-${it.rule_id}`} className="flex flex-wrap items-center gap-2 pl-5" data-testid={`paie-run-ccq-issue-${i}`}>
              <span><b>{it.employee_name}</b> — « {it.label} » ({it.payer === "employee" ? "salarié" : "employeur"}) : {it.reason}</span>
              <FixRuleLink ruleId={it.rule_id} masterId={masterId} testId={`paie-run-ccq-issue-fix-${i}`} />
            </div>
          ))}
          <p className="pl-5 text-[10px] text-rose-700">Après correction de la règle, revenez ici et cliquez « Recalculer ».</p>
        </div>
      )}

      {(run.warnings || []).filter((w) => !((run.ccq_issues || []).length && w.includes(" — CCQ : Cotisation « "))).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 space-y-0.5" data-testid="paie-run-warnings">
          {run.warnings.filter((w) => !((run.ccq_issues || []).length && w.includes(" — CCQ : Cotisation « "))).map((w, i) => <div key={i} className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{w}</span></div>)}
        </div>
      )}

      {["approved", "paid"].includes(run.status) && <RunExports masterId={masterId} run={run} settings={settings} onChanged={load} />}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs font-bold text-slate-700">Feuille de temps — heures & montants de la période</span>
          {!locked && addable.length > 0 && (
            <div className="flex items-center gap-2">
              <select className="px-2 py-1 rounded-lg border border-slate-300 text-xs" value={addId} onChange={(e) => setAddId(e.target.value)} data-testid="paie-run-add-select">
                <option value="">Ajouter un employé…</option>{addable.map((e) => <option key={e.id} value={e.id}>{`${e.display_name} (${FREQ_LABELS[e.pay_frequency]})`}</option>)}
              </select>
              <Btn tone="slate" icon={UserPlus} onClick={addEmp} disabled={!addId} busy={busy === "add"} testId="paie-run-add-emp">Ajouter</Btn>
            </div>
          )}
        </div>
        <div className="overflow-x-auto" ref={scrollRef}>
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200"><tr>
              <th className={thCls}></th><th className={thCls}>Employé</th>
              {HOUR_COLS.map(([k, l]) => <th key={k} className={`${thCls} text-right`}>{l}</th>)}
              {AMT_COLS.map(([k, l]) => <th key={k} className={`${thCls} text-right`}>{l}</th>)}
              <th className={`${thCls} text-right`}>Brut</th><th className={`${thCls} text-right`}>Net</th><th className={thCls}></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map(({ employee: e, stub }) => {
                const f = forms[e.id]; if (!f) return null;
                const isOpen = !!open[e.id];
                return (
                  <FragmentRow key={e.id} e={e} f={f} stub={stub} isOpen={isOpen} locked={locked} dirty={!!dirty[e.id]} busy={busy}
                    onToggle={() => setOpen((o) => ({ ...o, [e.id]: !o[e.id] }))} setField={setField} removeEmp={removeEmp} dedTypes={dedTypes} ccq={ccq} asOf={run.period_end} visibleW={visibleW} />
                );
              })}
            </tbody>
          </table>
        </div>
        {data.rows.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">Aucun employé dans cette période.</p>}
      </div>
    </div>
  );
}

function FragmentRow({ e, f, stub, isOpen, locked, dirty, busy, onToggle, setField, removeEmp, dedTypes, ccq, asOf, visibleW }) {
  const splitKeys = (f.ccq_sites || []).length > 0 ? new Set(["regular_hours", "ot15_hours", "ot2_hours"]) : null;
  const cell = (k, step = "0.25") => (
    <td key={k} className="px-1 py-1">
      <input type="number" step={step} min="0" disabled={locked || f.excluded || !!splitKeys?.has(k)} title={splitKeys?.has(k) ? "Heures réparties par chantier (voir le détail)" : undefined}
        className={`w-20 border rounded-md px-2 py-1 text-right text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-300 ${locked ? "bg-slate-50 text-slate-500 border-slate-200" : "border-slate-300"}`}
        value={f[k]} onChange={(ev) => setField(e.id, k, ev.target.value)} data-testid={`paie-ts-${k}-${e.id}`} />
    </td>
  );
  return (
    <>
      <tr className={`${f.excluded ? "opacity-50" : ""} ${dirty ? "bg-amber-50/40" : ""}`} data-testid={`paie-ts-row-${e.id}`}>
        <td className="px-2"><button onClick={onToggle} className="p-1 rounded hover:bg-slate-100" aria-label="Détails" data-testid={`paie-ts-toggle-${e.id}`}>{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button></td>
        <td className={`${tdCls} whitespace-nowrap`}><div className="font-semibold text-slate-800">{e.display_name}</div>
          <div className="text-[11px] text-slate-500">{e.employee_no} · {e.pay_type === "salary" ? `${fmtCAD(e.annual_salary)}/an` : `${fmtCAD(e.hourly_rate)}/h`}{(e.extra_rates || []).length > 0 ? ` · ${e.extra_rates.length + 1} taux` : ""}{e.ccq?.active ? " · CCQ" : ""}{(f.ccq_sites || []).length > 0 ? ` · ${f.ccq_sites.length} chantier(s)` : ""}</div>
          {(e.extra_rates || []).length > 0 && (f.rate_hours || []).some((x) => num(x.regular_hours) + num(x.ot15_hours) + num(x.ot2_hours) > 0) && (
            <div className="text-[10px] text-sky-700" data-testid={`paie-ts-rates-hint-${e.id}`}>+ {(f.rate_hours || []).reduce((a, x) => a + num(x.regular_hours) + num(x.ot15_hours) + num(x.ot2_hours), 0)} h aux taux additionnels (détail)</div>
          )}</td>
        {HOUR_COLS.map(([k]) => cell(k))}
        {AMT_COLS.map(([k]) => cell(k, "0.01"))}
        <td className={`${tdCls} text-right font-mono whitespace-nowrap`}>{stub ? fmtCAD(stub.result.gross) : <span className="text-slate-300">—</span>}</td>
        <td className={`${tdCls} text-right font-mono font-bold whitespace-nowrap`} data-testid={`paie-ts-net-${e.id}`}>{stub ? fmtCAD(stub.result.net) : <span className="text-slate-300">—</span>}</td>
        <td className="px-2 whitespace-nowrap">
          {!locked && <input type="checkbox" checked={!!f.excluded} onChange={(ev) => setField(e.id, "excluded", ev.target.checked)} title="Exclure de cette paie (aucun bulletin)" className="w-4 h-4 accent-slate-500 mr-1 align-middle" data-testid={`paie-ts-excluded-${e.id}`} />}
          {!locked && <button onClick={() => removeEmp(e.id)} disabled={busy === `rm-${e.id}`} className="p-1 rounded text-rose-500 hover:bg-rose-50" title="Retirer de la période" data-testid={`paie-ts-remove-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-white"><td colSpan={HOUR_COLS.length + AMT_COLS.length + 5} className="px-4 py-3">
          {/* le détail reste dans la zone visible même si la feuille de temps défile horizontalement */}
          <div className="sticky left-0 space-y-3" style={visibleW ? { width: Math.max(visibleW - 32, 320) } : undefined}>
            <ExtraRow f={f} set={(k, v) => setField(e.id, k, v)} dedTypes={dedTypes} locked={locked} eid={e.id} ccq={ccq} employee={e} asOf={asOf} />
            {stub && <StubBreakdown result={stub.result} ytd={stub.ytd_before} ytdLines={stub.ytd_lines} employerLines={stub.employer_lines} />}
            {stub?.cheque_no && <p className="text-[11px] text-slate-500">Chèque n° {stub.cheque_no}</p>}
          </div>
        </td></tr>
      )}
    </>
  );
}
